/**
 * State for the Common Balance Pool builder, following the package's builder-state convention
 * (cf. TPAllocationState / CalculationBuilderState): entity + isLoading/error, a `needsRefresh` flag
 * that drives the fetch effect, and the cross-component modal / lifecycle state.
 */
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { CommonBalancePool, CommonBalancePoolColumn, CommonBalancePoolExecutionStatus, CommonBalancePoolRow } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { SegmentTableDefinition } from 'src/models/segment-cdt/SegmentCdtTypes';

/**
 * One Step-1 input row: a source object and the columns it contributes (matching the CBP mockup).
 * Owned here in the service layer so `editingRow` doesn't create a service→component dependency;
 * the component-side helpers in `cbpInputs.ts` import and re-export this type.
 */
export interface InputRow {
    num: number;
    sourceType?: string;
    sourceObject: string;
    sourceKey: string;
    /** Display names, for the Columns badges. */
    columns: string[];
    /** The underlying INPUT columns backing this row, so Edit can rebuild them. */
    cols: CommonBalancePoolColumn[];
}

export interface CommonBalancePoolBuilderState {
    pool?: CommonBalancePool;
    isLoading: boolean;
    error?: string;
    /** Set true to (re)fetch the pool; the reducer clears it on SET_POOL. */
    poolNeedsRefresh: boolean;

    /** True while a column persist (UpdateCommonBalancePool) is in flight. */
    saving: boolean;
    saveError?: string;

    /** True while Step 2 (calculated columns) has unsaved edits — guards the lifecycle/input actions. */
    step2Dirty: boolean;

    /** Add/Edit-input modal. `editingRow` present => edit mode for that input. */
    addModalVisible: boolean;
    editingRow?: InputRow;

    /** Selected accounting period (for Execute). */
    period?: SelectProps.Option | null;

    /**
     * The backend's current open accounting period (yyyyMM), from MECService.getHandOffStatus — the same
     * source the promotion-history view anchors to. Only this period is editable; every other selected
     * period is a historical view rendered read-only from the version that produced it. Absent while the
     * hand-off read is in flight or if it failed, in which case editing is not locked (fail open).
     */
    openPeriod?: string;

    /** Which top-level lifecycle action is in flight. */
    actionBusy: 'save' | 'submit' | 'execute' | 'approve' | 'reject' | '';
    actionError?: string;
    actionNotice?: string;

    /**
     * Live status of the current period's Execute run, polled from GetCommonBalancePoolExecutionStatus
     * (LLD §12.4) until a terminal state or the client-side timeout. Session-scoped and keyed to the run
     * the user triggered — cleared on a period switch, and gone on reload (recovering a period's last
     * status without an executionId is the deferred by-(poolId, period) backend read).
     */
    executionStatus?: CommonBalancePoolExecutionStatus;
    /**
     * True once the poll gave up without a terminal state — the 30-min client-side timeout, or repeated
     * status-read failures. Drives the "Reload status" recovery Alert and unblocks the Execute button. The
     * run may still be going server-side.
     */
    executionTimedOut?: boolean;

    /**
     * The segment-CDT list (the Step-1 input sources), fetched once by the builder and shared via
     * context: the builder derives an input source-name map from it, and the Add-input modal reads the
     * same list for its picker (rather than fetching it again).
     */
    segmentTables?: SegmentTableDefinition[];
    segmentTablesLoading?: boolean;
    segmentTablesError?: string;

    /**
     * Step-3 preview slice: the selected period's materialized rows, read a page at a time via
     * getCommonBalancePoolRows. Held here (not as component-local state) so the whole builder's state lives
     * in one reducer, per the package convention.
     */
    resultsRows: CommonBalancePoolRow[];
    resultsNextToken?: string;
    resultsTotalRowCount?: number;
    /**
     * The selected period's column totals, from the FIRST page of GetCommonBalancePoolRows. Server-computed over
     * the whole period; never summed from the loaded pages, which would be a page total shown as the pool's.
     */
    resultsTotals?: Record<string, number>;
    resultsLoading: boolean;
    resultsError?: string;
    /**
     * Columns of the definition version that produced the selected period, for the grid HEADERS +
     * calc-column formulas (the VALUES are the period's rows). null/undefined => use the working definition
     * (pool.columns). Resolved per period by CbpResultsSection and held here with the rest of the Step-3
     * slice, per the package convention (not component-local state).
     */
    resultsVersionedColumns?: CommonBalancePoolColumn[] | null;
    /**
     * The definition version NUMBER that produced the selected past period (from GetCommonBalancePoolPeriod),
     * so the version indicator shows the period's own version rather than the pool's current one. Undefined
     * for the open period or until it resolves. Resolved together with resultsVersionedColumns.
     */
    resultsPeriodVersion?: number;
    /**
     * True while the selected period's definition version is being resolved (the two async reads). Lets the
     * builder show a brief loading state for a past period instead of flashing the working definition before
     * that period's own version arrives.
     */
    resultsVersionResolving?: boolean;
    /**
     * Whether the selected period is materialised/published — from GetCommonBalancePoolPeriod answering
     * (200 => true, throw => false), independent of whether its rows have loaded. Drives the past-period
     * status so a transient rows-read failure doesn't report a published period as "Not materialised".
     */
    resultsPeriodMaterialised?: boolean;
    /**
     * Bumped to force the Step-3 preview to reset + refetch the selected period — e.g. after an Execute,
     * so the newly materialized rows load without the user re-selecting the period. CbpResultsSection's
     * fetch effect depends on it alongside period/poolId.
     */
    resultsRefreshToken: number;

    /** Step-3 "Download" export: true while the presigned-URL request is in flight. */
    resultsDownloading: boolean;
    resultsDownloadError?: string;
}

export const initialState: CommonBalancePoolBuilderState = {
    isLoading: true,
    poolNeedsRefresh: true,
    saving: false,
    step2Dirty: false,
    addModalVisible: false,
    actionBusy: '',
    resultsRows: [],
    resultsLoading: false,
    resultsRefreshToken: 0,
    resultsDownloading: false,
};

export const getInitialState = (): CommonBalancePoolBuilderState => ({ ...initialState });

/** The selected period is the backend open period (known and equal). */
const isOpenPeriodSelected = (state: CommonBalancePoolBuilderState): boolean =>
    !!state.openPeriod && !!state.period?.value && state.period.value === state.openPeriod;

/**
 * A confirmed PAST period: the open period is known AND a different period is selected. Distinct from "open
 * period unknown" (hand-off read in flight/failed), where we must fail open to the working definition rather
 * than treat the period as historical. Exported so the builder reuses it rather than re-deriving the rule.
 */
export const isPastPeriodView = (state: CommonBalancePoolBuilderState): boolean =>
    !!state.openPeriod && !!state.period?.value && state.period.value !== state.openPeriod;

/** Shared copy: why the builder is read-only on a past period, and the past-period empty/unavailable state. */
export const READ_ONLY_PAST_PERIOD_REASON =
    'This is a past period shown read-only. Select the open period to make changes.';
export const PERIOD_DEFINITION_UNAVAILABLE = 'The definition for this period is unavailable.';

/**
 * The display state of the selected period's definition, so all three steps share one resolving / unavailable
 * / available split: 'available' for the open period (working def) or a past period whose version resolved;
 * 'resolving' while a past period's version is being fetched; 'unavailable' for a past period that resolved to
 * no version (e.g. lane-0 / pre-provenance periods with no archived definition).
 */
export type PeriodDefinitionState = 'resolving' | 'unavailable' | 'available';
export const periodDefinitionState = (state: CommonBalancePoolBuilderState): PeriodDefinitionState => {
    if (!isPastPeriodView(state)) return 'available';
    if (state.resultsVersionResolving) return 'resolving';
    return (state.resultsVersionedColumns && state.resultsVersionedColumns.length > 0) ? 'available' : 'unavailable';
};

/**
 * The definition columns to render for the selected period. The OPEN period always shows the WORKING
 * definition (the draft being built). A PAST period shows the version that produced it
 * (resultsVersionedColumns) — and, crucially, does NOT fall back to the working definition when that is not
 * yet resolved: it returns an empty set so a period switch never flashes the current formula before the
 * period's own version arrives (the builder shows a loading state meanwhile via resultsVersionResolving).
 */
export const effectiveDefinitionColumns = (state: CommonBalancePoolBuilderState): CommonBalancePoolColumn[] => {
    // A resolved version (for any non-open period) always wins.
    if (!isOpenPeriodSelected(state) && state.resultsVersionedColumns) return state.resultsVersionedColumns;
    // A confirmed PAST period whose version has NOT resolved yet shows EMPTY (loading) — never the working
    // definition, so a switch does not flash the current formula before the period's own version arrives.
    if (isPastPeriodView(state)) return [];
    // Open period, or open period unknown (fail open): the working definition.
    return state.pool?.columns ?? [];
};

/**
 * Whether the selected period is editable. The invariant is **editable ⟺ the working definition is what is
 * shown**, so an edit can never be saved over the wrong version: editable only for the confirmed open period,
 * or — when the open period is unknown (hand-off read in flight/failed) — a period with no resolved historical
 * version (a normal draft), which fails open so that read never locks ordinary editing. A resolved past-version
 * view (effectiveDefinitionColumns returning resultsVersionedColumns) is therefore always read-only.
 */
export const isPeriodEditable = (state: CommonBalancePoolBuilderState): boolean =>
    isOpenPeriodSelected(state) || (!state.openPeriod && !state.resultsVersionedColumns);
